import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Confirmación para eliminar una feria (PLAN_FERIAS). En el backend es un
 * borrado lógico: el link y el QR muestran "Esta feria ya no está disponible";
 * las vacantes y las postulaciones no cambian. Como en las vacantes, "Eliminar"
 * se habilita recién al escribir la palabra de confirmación (ELIMINAR / DELETE).
 *
 * Props: open, onClose, fairId, fairName, onDeleted(fairId)
 */
const AtsDeleteFairDialog = ({ open, onClose, fairId, fairName, onDeleted }) => {
  const { t } = useTranslation();
  const { authToken, showAlert, organization } = useStore.getState();
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const confirmWord = t('ats.positions.deleteDialog.confirmWord');
  const confirmed = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();

  // Cada apertura parte con el campo vacío
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const handleConfirm = async () => {
    if (!confirmed) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/organizations/job-fairs/${fairId}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('ats.fairs.deleteDialog.success'), 'success');
        onClose();
        if (onDeleted) onDeleted(fairId);
      } else {
        showAlert(t('ats.fairs.deleteDialog.error'), 'error');
      }
    } catch (err) {
      console.error('Error eliminando feria:', err);
      showAlert(t('ats.fairs.deleteDialog.error'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('ats.fairs.deleteDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ color: 'text.primary' }}>
          {t('ats.fairs.deleteDialog.confirm', { name: fairName || '' })}
        </DialogContentText>
        <DialogContentText sx={{ mt: 1, fontWeight: 600, color: '#B45309' }}>
          {t('ats.fairs.deleteDialog.warning')}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          sx={{ mt: 2 }}
          label={t('ats.positions.deleteDialog.typeToConfirm', { word: confirmWord })}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && confirmed && !deleting) handleConfirm(); }}
          disabled={deleting}
          inputProps={{ autoComplete: 'off' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={deleting}>
          {t('cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleting || !confirmed}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AtsDeleteFairDialog;
